"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useId, useCallback } from 'react';
import { ButtonGroup } from '../../buttons/button-group/index.js';
import { validateSurface } from '../../globals.js';
import CheckmarkAltBold from '../../icons/component-icons/checkmark-alt-bold/index.js';
import ErrorBold from '../../icons/component-icons/error-bold/index.js';
import InfoBold from '../../icons/component-icons/info-bold/index.js';
import WarningBold from '../../icons/component-icons/warning-bold/index.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import useViewport from '../../utilities/use-viewport/index.js';
import NotificationClose from './notification-close.js';
import styles from './notification.module.scss.js';

const Notification = (_a) => {
    var { id, disableAnimation = false, kind = 'information', orientation = 'vertical', opened, showCloseButton: showCloseButtonProp, disableFocus = false, fullBleed = false, viewport: viewportProp, surface: surfaceProp = 'lightPrimary', onCloseButtonClick, title, bodyText, buttons, children, contentAreaMaxWidth = '1232px', 'data-testid': testId = 'notification' } = _a, rest = __rest(_a, ["id", "disableAnimation", "kind", "orientation", "opened", "showCloseButton", "disableFocus", "fullBleed", "viewport", "surface", "onCloseButtonClick", "title", "bodyText", "buttons", "children", "contentAreaMaxWidth", 'data-testid']);
    const internals = useRef();
    const closeButtonRef = useRef(null);
    const viewport = useViewport('standard', viewportProp);
    const [role, setRole] = useState(undefined);
    const [visibleState, setVisible] = useState(true);
    const [unmounted, setUnmounted] = useState(false);
    const [isFirstFocus, setIsFirstFocus] = useState(true);
    const [isAppleDevice, setIsAppleDevice] = useState(false);
    const [visible, setIsVisible] = useState(false);
    const [showCloseButton, setShowCloseButton] = useState(false);
    const surface = validateSurface(surfaceProp);
    useEffect(() => {
        if (kind === 'error') {
            setShowCloseButton(showCloseButtonProp !== undefined || false);
        }
        setShowCloseButton(showCloseButtonProp !== undefined ? showCloseButtonProp : true);
    }, [showCloseButtonProp, kind]);
    useEffect(() => {
        const os = getOS();
        setIsAppleDevice(os === 'osx' || os === 'ios');
        setIsVisible(opened === undefined ? visibleState : opened);
    }, [opened, visibleState]);
    const _useId = useId();
    const _isMobileDevice = checkIfMobileDevice();
    if (internals.current === undefined) {
        internals.current = {
            titleID: `title_${_useId}`,
            bodyTextID: `bodyText_${_useId}`,
            typeID: `type_${_useId}`,
            buttonID: `button_${_useId}`,
            childrenId: `children_${_useId}`,
        };
    }
    const close = useCallback((e) => {
        var _a;
        if (!disableAnimation) {
            setUnmounted(true);
            onCloseButtonClick === null || onCloseButtonClick === void 0 ? void 0 : onCloseButtonClick(e);
            internals.current.unMountTimer = setTimeout(() => {
                setVisible(false);
            }, 350);
        }
        else {
            setVisible(false);
            onCloseButtonClick === null || onCloseButtonClick === void 0 ? void 0 : onCloseButtonClick(e);
        }
        (_a = internals.current.wrapper) === null || _a === void 0 ? void 0 : _a.blur();
    }, [disableAnimation, onCloseButtonClick]);
    const keydownHandler = useCallback((e) => {
        if (e.key === 'Escape' && close) {
            close(e);
        }
    }, [close]);
    useEffect(() => {
        internals.current.roleTimer = setTimeout(() => {
            setRole('text');
        }, 350);
        const notification = id
            ? document.getElementById(id)
            : document.getElementById(`notification${_useId}`);
        notification === null || notification === void 0 ? void 0 : notification.addEventListener('keydown', keydownHandler);
        setTimeout(() => {
            setIsFirstFocus(false);
        }, 500);
        return () => {
            clearTimeout(internals.current.unMountTimer);
            clearTimeout(internals.current.roleTimer);
            notification === null || notification === void 0 ? void 0 : notification.removeEventListener('keydown', keydownHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const { buttonID } = internals.current;
        const { current: closeButton } = closeButtonRef;
        const buttonElements = typeof document !== 'undefined' && document.getElementById(buttonID);
        if (!disableFocus && closeButton)
            closeButton.focus();
        /* istanbul ignore if -- @preserve */
        if (!disableFocus && !showCloseButton && buttonElements)
            buttonElements.focus();
    }, [showCloseButton, disableFocus]);
    useEffect(() => {
        setUnmounted(false);
    }, [opened]);
    const renderIconElement = () => {
        const { 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': trackIgnore, 'data-clickstream': clickStream, 'data-sitecat-cta': cta, 'data-sitecat-datatrack': dataTrack, 'data-sitecat-position': position, 'data-sitecat-level': level, } = rest;
        /* istanbul ignore if -- @preserve */
        if (!showCloseButton)
            return null;
        return (jsx(NotificationClose, { ref: closeButtonRef, controlFunc: close, "data-analyticstrack": analyticsTrack, "data-track": track, "data-track-ignore": trackIgnore, "data-clickstream": clickStream, "data-sitecat-level": level, "data-sitecat-position": position, "data-sitecat-datatrack": dataTrack, "data-sitecat-cta": cta, isFirstFocus: isFirstFocus, surface: surface, viewport: viewport }));
    };
    const renderNotificationIcon = () => {
        const _getIcons = () => {
            switch (kind) {
                case 'warning':
                    return WarningBold;
                case 'error':
                    return ErrorBold;
                case 'success':
                    return CheckmarkAltBold;
                case 'information':
                default:
                    return InfoBold;
            }
        };
        const Icon = _getIcons();
        return (jsx("span", { className: styles.iconWrapper, tabIndex: -1, "aria-hidden": true, children: jsx("div", { className: styles.icon, tabIndex: -1, "aria-hidden": true, "aria-label": `${kind} icon`, children: jsx(Icon, { size: "100%" }) }) }));
    };
    const renderText = () => {
        if (title || bodyText) {
            return (jsxs("div", { className: styles.textIcon, children: [renderNotificationIcon(), jsxs("div", { className: styles.textWrapper, "aria-labelledby": disableFocus ? internals.current.typeID : undefined, "aria-live": isFirstFocus ? 'polite' : 'off', role: isAppleDevice ? 'text' : role, children: [jsx("span", { className: styles.accessibilityLabel, "aria-hidden": isAppleDevice && disableFocus, id: internals.current.typeID, children: isAppleDevice && disableFocus
                                    ? `${disableFocus ? 'Alert!' : ''} ${kind} notification ${title} ${bodyText}`
                                    : `${disableFocus ? 'Alert!' : ''} ${kind} notification` }), title && (jsx("p", { className: `${styles.title} ${styles.padding}`, id: internals.current.titleID, children: title })), bodyText && (jsx("p", { className: styles.bodyText, id: internals.current.bodyTextID, children: bodyText }))] })] }));
        }
        return (jsxs("div", { className: styles.textIcon, children: [renderNotificationIcon(), jsxs("div", { className: styles.textWrapper, "aria-labelledby": disableFocus ? internals.current.typeID : undefined, "aria-live": isFirstFocus ? 'polite' : 'off', role: isAppleDevice ? 'text' : role, children: [jsxs("span", { className: styles.accessibilityLabel, "aria-hidden": isAppleDevice && disableFocus, id: internals.current.typeID, children: [`${disableFocus ? 'Alert!' : ''} ${kind} notification`, isAppleDevice && disableFocus && children] }), jsx("div", { className: styles.titleCustom, id: internals.current.childrenId, children: children })] })] }));
    };
    const parseButtons = () => {
        const buttonDetail = [];
        if (buttons && buttons.length > 2)
            buttons.splice(2);
        buttons === null || buttons === void 0 ? void 0 : buttons.forEach((data, index) => {
            const item = Object.assign(Object.assign({}, data), { surface });
            if (index === 0) {
                item.id = internals.current.buttonID;
                if (_isMobileDevice && !showCloseButton && isFirstFocus) {
                    item['aria-live'] = 'polite';
                    item['aria-label'] = `${item.children} button`;
                }
            }
            if ((index === 0 && !('buttonType' in data) && !('kind' in data)) ||
                index === 1) {
                item.kind = item.kind || 'secondary';
            }
            item.size = 'small';
            buttonDetail.push(item);
        });
        return buttonDetail;
    };
    const renderButtons = () => (jsx("div", { className: styles.buttonAreaWrapper, children: buttons && (jsx(ButtonGroup, { alignment: "left", viewport: viewport, surface: surface, items: parseButtons() })) }));
    const renderContentArea = () => (jsx("div", { className: styles.content, children: _isMobileDevice ? (renderText()) : (jsx("div", { role: isAppleDevice || !disableFocus ? 'alert' : undefined, children: renderText() })) }));
    const renderActionsArea = () => {
        if (buttons && !!buttons.length) {
            const actionButtons = [styles.actions];
            if (orientation === 'horizontal')
                actionButtons.push(styles.horizontal);
            return jsx("div", { className: actionButtons.join(' '), children: renderButtons() });
        }
    };
    const renderAlert = () => {
        if (!disableAnimation && !visible)
            return null;
        const animationStyle = disableAnimation
            ? {
                opacity: visible ? 1 : 0,
                height: visible ? 'auto' : 0,
                display: visible ? 'flex' : 'none',
            }
            : {};
        const { 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': trackIgnore, 'data-clickstream': clickStream, 'data-sitecat-cta': cta, 'data-sitecat-datatrack': dataTrack, 'data-sitecat-position': position, 'data-sitecat-level': level, } = rest;
        const container = [
            `${styles.container} ${!buttons || buttons.length === 0 ? styles.noActions : ''}`,
            styles[surface.toLowerCase()],
            styles[kind],
        ];
        if (unmounted)
            container.push(styles.unmounted);
        if (fullBleed)
            container.push(styles.fullBleed);
        if (disableAnimation)
            container.push(styles.disableAnimation);
        if (orientation === 'horizontal')
            container.push(styles.horizontal);
        return (jsxs("div", { style: Object.assign(Object.assign({}, animationStyle), { maxWidth: !fullBleed ? contentAreaMaxWidth : undefined }), className: container.join(' '), "data-testid": testId, "data-analyticstrack": analyticsTrack, "data-track": track, "data-track-ignore": trackIgnore, "data-clickstream": clickStream, "data-sitecat-level": level, "data-sitecat-cta": cta, "data-sitecat-datatrack": dataTrack, "data-sitecat-position": position, tabIndex: -1, children: [renderIconElement(), renderContentArea(), renderActionsArea()] }));
    };
    const baseStyles = [styles.notification, styles[viewport]];
    if (fullBleed)
        baseStyles.push(styles.fullBleed);
    return (jsx("div", { className: baseStyles.join(' '), id: id || `notification${_useId}`, tabIndex: -1, ref: (input) => {
            internals.current.wrapper = input;
        }, children: renderAlert() }));
};

export { Notification, Notification as default };
